import * as XLSX from 'xlsx';

/* ── Formatting helpers ──────────────────────────────────────── */
const formatHours = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return '—';
  return `${Number(n.toFixed(2))} h`;
};

const formatLateHours = (minutes) => {
  const n = Number(minutes);
  if (!Number.isFinite(n) || n <= 0) return '—';
  return `${Number((n / 60).toFixed(2))}h`;
};

/* ── Column definitions ──────────────────────────────────────── */
export const OT_EXPORT_COLUMNS = [
  { key: 'employeeName',      label: 'Employee',           width: 25 },

  { key: 'date',              label: 'Date',               width: 14 },

  { key: 'otTypeLabel',       label: 'OT Type',            width: 18 },

  { key: 'inTime',            label: 'In Time',            width: 12 },

  { key: 'outTime',           label: 'Out Time',           width: 12 },

  { key: 'lateMinutes',       label: 'Late Hours',         width: 14, minutes: true },

  { key: 'totalHours',        label: 'Total Hours',        width: 14, time: true },

  { key: 'normalHours',       label: 'Normal Hours',       width: 14, time: true },

  { key: 'preShiftOTHours',   label: 'Pre OT Hours',       width: 14, time: true },

  { key: 'postOTHours',       label: 'Post OT Hours',      width: 14, time: true },

  // { key: 'prePostOTHours',    label: 'Pre & Post OT Hours', width: 20, time: true },

  { key: 'holidayOTHours',    label: 'Holiday OT Hours',   width: 17, time: true },

  { key: 'sundayOTHours',     label: 'Sunday OT Hours',    width: 17, time: true },

  { key: 'otHours',           label: 'OT Hours',           width: 14, time: true },

  { key: 'otRate',            label: 'OT Rate',            width: 14, numeric: true },

  { key: 'otAmount',          label: 'OT Amount',          width: 16, numeric: true },

  { key: 'status',            label: 'Status',             width: 14 },
];

/* ── Utilities ───────────────────────────────────────────────── */
function pad2(n) {
  return String(n).padStart(2, '0');
}

function stamp(d = new Date()) {
  return (
    `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())} ` +
    `${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`
  );
}

function numberValue(value) {
  if (value === null || value === undefined || value === '') return 0;
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
}

function getStatus(row) {
  if (row.status !== undefined && row.status !== null && row.status !== '') {
    return row.status;
  }
  if (row.isApproved !== undefined) {
    return row.isApproved ? 'Approved' : 'Pending';
  }
  return '';
}

/* ── Export ──────────────────────────────────────────────────── */
export function exportOTRecordsToExcel({ rows = [], filters = {} }) {
  const activeFilters =
    Object.entries(filters)
      .filter(([, value]) => value && value !== 'All' && value !== 'None')
      .map(([key, value]) => `${key}: ${value}`)
      .join('   |   ') || 'None';

  const headerRows = [
    ['OVERTIME RECORDS'],
    [`Filters: ${activeFilters}`],
    [`Generated: ${stamp()}`],
    [`Records: ${rows.length}`],
    [],
  ];

  const columnHeader = OT_EXPORT_COLUMNS.map((column) => column.label);

  /* ── Body rows ── */
  const bodyRows = rows.map((row) =>
    OT_EXPORT_COLUMNS.map((column) => {
      const key = column.key;

      if (key === 'status') return getStatus(row);

      if (key === 'otTypeLabel') {
        return String(row.otTypeLabel ?? '').trim();
      }

      // Late Hours → decimal (e.g. "7.77h")
      if (column.minutes) {
        return formatLateHours(row[key]);
      }

      // Other hour columns → "7.77 h"
      if (column.time) {
        return formatHours(row[key]);
      }

      // Numeric (rates / amounts)
      if (column.numeric) {
        return numberValue(row[key]);
      }

      // Plain text
      const value = row[key];
      return value === null || value === undefined ? '' : value;
    })
  );

  /* ── Totals row ── */
  const round2 = (value) =>
    Math.round((value + Number.EPSILON) * 100) / 100;

  const totalsRow = OT_EXPORT_COLUMNS.map((column, index) => {
    if (index === 0) {
      return `TOTAL — ${rows.length} record${rows.length === 1 ? '' : 's'}`;
    }

    // Late Hours
    if (column.minutes) {
      const totalMinutes = rows.reduce(
        (sum, row) => sum + numberValue(row[column.key]),
        0
      );
      return formatLateHours(totalMinutes);
    }

    // Other hour columns
    if (column.time) {
      const totalHours = rows.reduce(
        (sum, row) => sum + numberValue(row[column.key]),
        0
      );
      return formatHours(totalHours);
    }

    // OT Amount
    if (column.key === 'otAmount') {
      return round2(
        rows.reduce((sum, row) => sum + numberValue(row[column.key]), 0)
      );
    }

    // OT Rate
    return '';
  });

  const aoa = [
    ...headerRows,
    columnHeader,
    ...bodyRows,
    [],
    totalsRow,
  ];

  const worksheet = XLSX.utils.aoa_to_sheet(aoa);

  worksheet['!cols'] = OT_EXPORT_COLUMNS.map((column) => ({
    wch: column.width || 12,
  }));

  const lastColumn = OT_EXPORT_COLUMNS.length - 1;

  worksheet['!merges'] = headerRows
    .slice(0, 4)
    .map((_, index) => ({
      s: { r: index, c: 0 },
      e: { r: index, c: lastColumn },
    }));

  const headerRowIndex = headerRows.length;
  const firstBodyRow = headerRowIndex + 1;
  const lastBodyRow = firstBodyRow + bodyRows.length - 1;

  for (let rowIndex = firstBodyRow; rowIndex <= lastBodyRow; rowIndex++) {
    OT_EXPORT_COLUMNS.forEach((column, columnIndex) => {
      if (!column.numeric) return;

      const cellAddress = XLSX.utils.encode_cell({
        r: rowIndex,
        c: columnIndex,
      });

      if (worksheet[cellAddress]) {
        worksheet[cellAddress].z = '0.00';
      }
    });
  }

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'OT Records');

  const output = XLSX.write(workbook, {
    bookType: 'xlsx',
    type: 'array',
  });

  const blob = new Blob([output], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });

  const date = new Date();
  const filename =
    `OT_Records_${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}.xlsx`;

  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');

  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);

  setTimeout(() => URL.revokeObjectURL(url), 1000);

  return filename;
}